import { createElement as h, useEffect, useState, type ReactNode } from "react";
import type { BindingCtx, DomainRead } from "../../../state/editor/types";
import type { ParallaxCameraConfig, ParallaxMappingConfig } from "../../../shared/api-contract";
import type { SectionDef } from "../inspector/schema";
import { CompactCheck, CompactSlider, Row } from "../inspector/rows";
import { ObjectRotationsControl, ExtrudeTubeControl, SimplifyControl } from "./custom/ModifierControls";
import { SimpleTextControl } from "./custom/SimpleTextControl";

/**
 * Modifiers tab (v16 + v23). Object Rotations (radial duplication), Extrude Tube
 * Shapes, full Simple Text, Parallax mapping/camera as two verbatim-titled
 * checklist sections, Simplify (meshopt-style decimation) last. Auto Pose
 * moved to the Animations tab as "Pose Plugin" (checklist placement); the
 * HDRi Ground duplicate (of the Scene-tab section) was dropped.
 */

/** `Row` requires `children` in props, so pass it explicitly. */
const row = (label: string, child: ReactNode) => h(Row, { label, children: child });

/** Parallax Mapping (checklist verbatim title). Was merged with Parallax
 * Camera Controller into one "Parallax Mapping / Camera" section inside
 * ModifierControls.tsx's ParallaxControl; this queries getParallaxMapping/
 * setParallaxMapping directly so it can stand alone with the exact title. */
function ParallaxMappingRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const r = ctx.ring;
  const [m, setM] = useState<ParallaxMappingConfig | null>(null);
  useEffect(() => {
    r.getParallaxMapping().then(setM).catch(() => {});
  }, [r]);
  if (!m) return null;
  const set = (patch: Partial<ParallaxMappingConfig>) => r.setParallaxMapping(patch).then(setM);
  return h(
    "div",
    { className: "flex flex-col" },
    row("Enabled", h(CompactCheck, { checked: m.enabled, onChange: (v: boolean) => void set({ enabled: v }) })),
    // Two working shader debug views with no way to reach them: both fields are
    // on the contract and both are pushed as uniforms and branched on in
    // ParallaxMapping.ts — `uPDebugN` paints the encoded normal, `uPDebugH` the
    // hit height — and nothing rendered a toggle.
    row("Debug Normals", h(CompactCheck, { checked: m.debugNormals, onChange: (v: boolean) => void set({ debugNormals: v }) })),
    row("Debug Hit Height", h(CompactCheck, { checked: m.debugHitHeight, onChange: (v: boolean) => void set({ debugHitHeight: v }) })),
    row("Step count", h(CompactSlider, { value: m.stepCount, min: 1, max: 32, step: 1, onChange: (v: number) => void set({ stepCount: v }) })),
    row("Binary search steps", h(CompactSlider, { value: m.binaryStepCount, min: 1, max: 8, step: 1, onChange: (v: number) => void set({ binaryStepCount: v }) })),
  );
}

/** Parallax Camera Controller (checklist verbatim title) — split out from the
 * same merged panel; queries getParallaxCamera/setParallaxCamera directly. */
function ParallaxCameraRow({ ctx }: { ctx: BindingCtx; read: DomainRead }) {
  const r = ctx.ring;
  const [c, setC] = useState<ParallaxCameraConfig | null>(null);
  useEffect(() => {
    r.getParallaxCamera().then(setC).catch(() => {});
  }, [r]);
  if (!c) return null;
  const set = (patch: Partial<ParallaxCameraConfig>) => r.setParallaxCamera(patch).then(setC);
  return h(
    "div",
    { className: "flex flex-col" },
    row("Enabled", h(CompactCheck, { checked: c.enabled, onChange: (v: boolean) => void set({ enabled: v }) })),
    row("Invert", h(CompactCheck, { checked: c.invert, onChange: (v: boolean) => void set({ invert: v }) })),
    row("Sensitivity", h(CompactSlider, { value: c.sensitivity, min: 0.1, max: 2, step: 0.01, onChange: (v: number) => void set({ sensitivity: v }) })),
    row("Damping", h(CompactSlider, { value: c.damping, min: 0, max: 1, step: 0.001, onChange: (v: number) => void set({ damping: v }) })),
  );
}

export const modifiersSections: SectionDef[] = [
  {
    id: "objectRotations",
    title: "Object Rotations",
    rows: [{ id: "objrot", kind: "custom", label: "", domains: ["selection"], component: () => h(ObjectRotationsControl) }],
  },
  {
    id: "extrudeTube",
    title: "Extrude Tube Shapes",
    defaultCollapsed: true,
    rows: [{ id: "tube", kind: "custom", label: "", component: () => h(ExtrudeTubeControl) }],
  },
  {
    id: "simpleText",
    title: "Simple Text",
    defaultCollapsed: true,
    rows: [{ id: "text", kind: "custom", label: "", domains: ["selection"], component: () => h(SimpleTextControl) }],
  },
  {
    id: "parallaxMapping",
    title: "Parallax Mapping",
    defaultCollapsed: true,
    rows: [{ id: "parallaxMapping", kind: "custom", label: "", component: ParallaxMappingRow }],
  },
  {
    id: "parallaxCamera",
    title: "Parallax Camera Controller",
    defaultCollapsed: true,
    rows: [{ id: "parallaxCamera", kind: "custom", label: "", component: ParallaxCameraRow }],
  },
  {
    id: "simplify",
    title: "Simplify Modifier (meshopt)",
    defaultCollapsed: true,
    rows: [{ id: "simplify", kind: "custom", label: "", component: () => h(SimplifyControl) }],
  },
];
